import { ButtonPrimary, ButtonTertiary, RadioButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { Status } from "@nova/ds-component-status"
import { createQuery } from "@nova/queryable"
import {
    selectActiveWorkspaceId,
    selectDefaultChecklistId,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import { useState } from "react"
import { usePilotTrackingQuery } from "../PilotTrackingQueries"
import { getChecklistSubmitUrl } from "panels/routing/PilotTracking/PilotTrackingRoutes"
import { useRouter } from "pages"

const checklistsQuery = createQuery({
    resource: "checklists",
    select: ["id", "name", "description"],
})

export function getCompleteChecklistModalComponent({ missionIds }: { missionIds: string[] }) {
    const CompleteChecklistModal = ({
        confirm,
        cancel,
    }: {
        confirm: () => void
        cancel: () => void
        isPerformingAsyncConfirmCallback: boolean
        isPerformingAsyncCancelCallback: boolean
    }) => {
        const defaultChecklistId = useAppSelector(selectDefaultChecklistId)
        const [selectedFormId, setSelectedFormId] = useState<string | undefined>(
            defaultChecklistId ?? undefined
        )
        const orgId = useAppSelector(selectActiveWorkspaceId())
        const router = useRouter("/workspace/:workspaceId")
        const { data } = usePilotTrackingQuery(checklistsQuery)

        if (!data || !orgId) return null

        const isEmpty = data.length === 0

        const sorted = [...data].sort((a, b) => {
            if (a.id === defaultChecklistId) return -1
            if (b.id === defaultChecklistId) return 1
            return a.name.localeCompare(b.name)
        })

        return (
            <HighPriorityNovaModalContainer
                title={isEmpty ? "No Checklists Available" : "Select a Checklist"}
                closeModal={cancel}
                bottomBar={
                    <div className="w-full flex justify-end items-center gap-2">
                        <ButtonTertiary title="Cancel" onClick={cancel} />
                        {isEmpty ? (
                            <ButtonPrimary
                                title="Create Checklist"
                                LeftIcon={Icons.Plus}
                                onClick={() => {
                                    router.navigate({ path: "/fleet/checklists/create" })
                                    cancel()
                                }}
                            />
                        ) : (
                            <>
                                {selectedFormId ? (
                                    <a
                                        href={getChecklistSubmitUrl({
                                            orgId,
                                            checklistId: selectedFormId,
                                            missionIds,
                                        })}
                                        target="_blank"
                                        rel="noreferrer"
                                        onClick={confirm}
                                    >
                                        <ButtonPrimary
                                            title="Open Checklist"
                                            RightIcon={Icons.OpenInNew}
                                        />
                                    </a>
                                ) : (
                                    <ButtonPrimary
                                        title="Open Checklist"
                                        RightIcon={Icons.OpenInNew}
                                        disabled
                                    />
                                )}
                            </>
                        )}
                    </div>
                }
                className="w-full max-w-[610px]"
            >
                {isEmpty ? (
                    <div className="text-lg pt-2">
                        Create a checklist to standardize safety routines across your team.
                    </div>
                ) : (
                    <ScrollableArea className="max-h-[320px] w-full">
                        <div className="flex flex-col gap-2">
                            {sorted.map((d) => (
                                <div
                                    key={d.id}
                                    className="flex items-center gap-3 p-3 rounded-lg cursor-pointer hover:bg-gray-700 transition-colors border border-gray-700"
                                    onClick={() => setSelectedFormId(d.id)}
                                >
                                    <div className="h-fit">
                                        <RadioButton
                                            isSelected={selectedFormId === d.id}
                                            onClick={() => setSelectedFormId(d.id)}
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-white truncate">{d.name}</h3>
                                            {d.id === defaultChecklistId && (
                                                <Status type="positive" variant="outline">
                                                    Default
                                                </Status>
                                            )}
                                        </div>
                                        {d.description && (
                                            <small className="text-gray-300">{d.description}</small>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </ScrollableArea>
                )}
            </HighPriorityNovaModalContainer>
        )
    }
    return CompleteChecklistModal
}
